import API_BASE from '../../utils/api';
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import Pagination from "../../components/Pagination";
import ProfileDropdown from "../../components/ProfileDropdown";
import StudentProfileModal from "../../components/StudentProfileModal";

const emptyGuardian = () => ({ name: "", relation: "", phone: "", email: "", occupation: "", isPrimary: false, isEmergencyContact: false });

const IconEye = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);
const IconEdit = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);
const IconTrash = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const FormSection = ({ icon, title, children }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
    <h4 className="flex items-center gap-2 font-semibold text-gray-800">
      <span className="text-lg leading-none">{icon}</span> {title}
    </h4>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const FormField = ({ label, error, full, children }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const AdminStudents = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClass, setFilterClass] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const [formData, setFormData] = useState({
    name: "",
    rollNumber: "",
    admissionNumber: "",
    classId: "",
    dateOfBirth: "",
    gender: "male",
    placeOfBirth: "",
    category: "",
    feeGroup: "",
    address: "",
    phone: "",
    email: "",
    fatherName: "",
    motherName: "",
    guardianName: "",
    parentPhone: "",
    parentPhoneAlt: "",
    parentOccupation: "",
    parentName: "",
    parentEmail: "",
    parentPassword: "parent123",
    guardians: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [generatingAdmissionNo, setGeneratingAdmissionNo] = useState(false);

  // Categories/fee groups (from SchoolConfig) + school-defined custom fields
  const [studentCategories, setStudentCategories] = useState([]);
  const [feeGroups, setFeeGroups] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});

  // Sibling linking (from the registration/edit form)
  const [siblingSearch, setSiblingSearch] = useState("");
  const [siblingResults, setSiblingResults] = useState([]);
  const [searchingSiblings, setSearchingSiblings] = useState(false);
  const [selectedSiblings, setSelectedSiblings] = useState([]);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch students with filters and pagination
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Add class filter
      if (filterClass) {
        const selectedClass = classes.find((c) => c._id === filterClass);
        if (selectedClass) {
          params.append("class", selectedClass.className);
          params.append("section", selectedClass.section);
        }
      }

      // Add search term
      if (searchTerm) params.append("search", searchTerm);

      // Add pagination parameters
      params.append("page", pagination.currentPage);
      params.append("limit", pagination.pageSize);

      const response = await axios.get(
        `${API_BASE}/students?${params}`,
      );

      setStudents(response.data.students);
      setPagination({
        ...pagination,
        totalItems: response.data.pagination.total,
        totalPages: response.data.pagination.pages,
      });
    } catch (error) {
      console.error("Error fetching students:", error);
      setSubmitStatus({
        type: "error",
        message: "Failed to fetch students. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    filterClass,
    searchTerm,
    classes,
    pagination.currentPage,
    pagination.pageSize,
  ]);

  // Fetch classes
  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    fetchClasses();
    axios.get(`${API_BASE}/settings`).then((res) => {
      setStudentCategories(res.data?.studentCategories || []);
      setFeeGroups(res.data?.feeGroups || []);
    }).catch(() => {});
    axios.get(`${API_BASE}/custom-fields?appliesTo=student`).then((res) => {
      setCustomFieldDefs(res.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (classes.length > 0) {
      fetchStudents();
    }
  }, [classes, fetchStudents]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination({
      ...pagination,
      currentPage: newPage,
    });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPagination({
      currentPage: 1, // Reset to first page when changing page size
      pageSize: newSize,
      totalItems: pagination.totalItems,
      totalPages: Math.ceil(pagination.totalItems / newSize),
    });
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.rollNumber.trim())
      errors.rollNumber = "Roll number is required";
    if (!formData.classId) errors.classId = "Class is required";
    if (!formData.dateOfBirth) errors.dateOfBirth = "Date of birth is required";
    if (!editingStudent && !formData.admissionNumber.trim())
      errors.admissionNumber = "Admission number could not be generated — please retry";

    // Email format validation if provided
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    // Parent login is mandatory when creating a new student (the backend
    // requires it too); existing students can still be edited without it.
    if (!editingStudent && !formData.parentName.trim()) errors.parentName = "Parent name is required";
    if (!editingStudent && !formData.parentEmail.trim()) {
      errors.parentEmail = "Parent email is required";
    } else if (formData.parentEmail && !emailRegex.test(formData.parentEmail)) {
      errors.parentEmail = "Invalid email format";
    }
    // Parent phone feeds the mandatory User.phone field on the parent login
    // account — leaving it blank fails account creation on the backend.
    if (!editingStudent && !formData.parentPhone.trim()) {
      errors.parentPhone = "Parent phone is required";
    } else if (formData.parentPhone && !/^\d{10}$/.test(formData.parentPhone)) {
      errors.parentPhone = "Parent phone must be exactly 10 digits";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchNextAdmissionNumber = async () => {
    setGeneratingAdmissionNo(true);
    try {
      const res = await axios.get(`${API_BASE}/students/next-admission-number`);
      setFormData((f) => ({ ...f, admissionNumber: res.data.admissionNumber }));
    } catch (_) {
      // Leave blank; validation will block submission until it's available
    } finally {
      setGeneratingAdmissionNo(false);
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare data for submission
      const studentData = {
        ...formData,
        // Convert empty strings to undefined for optional fields
        admissionNumber: formData.admissionNumber || undefined,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        address: formData.address || undefined,
        placeOfBirth: formData.placeOfBirth || undefined,
        fatherName: formData.fatherName || undefined,
        motherName: formData.motherName || undefined,
        guardianName: formData.guardianName || undefined,
        parentPhone: formData.parentPhone || undefined,
        parentPhoneAlt: formData.parentPhoneAlt || undefined,
        parentOccupation: formData.parentOccupation || undefined,
        parentName: formData.parentName || undefined,
        parentEmail: formData.parentEmail || undefined,
        category: formData.category || undefined,
        feeGroup: formData.feeGroup || undefined,
        guardians: formData.guardians.filter((g) => g.name.trim()),
      };

      let savedStudentId = editingStudent?._id;

      if (editingStudent) {
        await axios.put(
          `${API_BASE}/students/${editingStudent._id}`,
          studentData,
        );
        setSubmitStatus({
          type: "success",
          message: "Student updated successfully!",
        });
      } else {
        const res = await axios.post(`${API_BASE}/students`, studentData);
        savedStudentId = res.data._id;
        setSubmitStatus({
          type: "success",
          message: res.data.emailSent
            ? "Student added successfully! A welcome email was sent to the parent."
            : "Student added successfully!",
        });
      }

      // Custom field values are validated/saved through their own endpoint
      if (savedStudentId && Object.keys(customFieldValues).length > 0) {
        try {
          await axios.put(`${API_BASE}/students/${savedStudentId}/custom-fields`, { values: customFieldValues });
        } catch (_) {
          // Non-fatal — the student record itself was saved successfully
        }
      }

      // Sibling links picked during registration/edit — same non-fatal
      // pattern, since the student record itself already saved successfully.
      if (savedStudentId && selectedSiblings.length > 0) {
        try {
          await axios.post(`${API_BASE}/students/${savedStudentId}/link-siblings`, {
            siblingStudentIds: selectedSiblings.map((s) => s._id),
          });
        } catch (_) {
          // Non-fatal
        }
      }

      setShowModal(false);
      setEditingStudent(null);
      resetForm();

      // Reset to first page and refresh data
      setPagination({
        ...pagination,
        currentPage: 1,
      });
      fetchStudents();
    } catch (error) {
      console.error("Error saving student:", error);
      setSubmitStatus({
        type: "error",
        message: error.response?.data?.error || "Error saving student",
      });
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this student?")) {
      try {
        await axios.delete(`${API_BASE}/students/${id}`);
        setSubmitStatus({
          type: "success",
          message: "Student deleted successfully!",
        });

        // If this was the last item on the page, go to previous page
        if (students.length === 1 && pagination.currentPage > 1) {
          setPagination({
            ...pagination,
            currentPage: pagination.currentPage - 1,
          });
        } else {
          fetchStudents();
        }
      } catch (error) {
        console.error("Error deleting student:", error);
        setSubmitStatus({ type: "error", message: "Error deleting student" });
      }
    }
  };

  // Handle edit
  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name || "",
      rollNumber: student.rollNumber || "",
      admissionNumber: student.admissionNumber || "",
      classId: student.classId?._id || "",
      dateOfBirth: student.dateOfBirth ? student.dateOfBirth.split("T")[0] : "",
      gender: student.gender || "male",
      placeOfBirth: student.placeOfBirth || "",
      category: student.category || "",
      feeGroup: student.feeGroup || "",
      address: student.address || "",
      phone: student.phone || "",
      email: student.email || "",
      fatherName: student.fatherName || "",
      motherName: student.motherName || "",
      guardianName: student.guardianName || "",
      parentPhone: student.parentPhone || "",
      parentPhoneAlt: student.parentPhoneAlt || "",
      parentOccupation: student.parentOccupation || "",
      parentName: student.parentId?.name || "",
      parentEmail: student.parentId?.email || "",
      guardians: student.guardians?.length ? student.guardians : [],
    });
    setCustomFieldValues({ ...(student.customFieldValues || {}) });
    setSelectedSiblings([]);
    setSiblingSearch("");
    setSiblingResults([]);
    setShowModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      rollNumber: "",
      admissionNumber: "",
      classId: "",
      dateOfBirth: "",
      gender: "male",
      placeOfBirth: "",
      category: "",
      feeGroup: "",
      address: "",
      phone: "",
      email: "",
      fatherName: "",
      motherName: "",
      guardianName: "",
      parentPhone: "",
      parentPhoneAlt: "",
      parentOccupation: "",
      parentName: "",
      parentEmail: "",
      parentPassword: "parent123",
      guardians: [],
    });
    setCustomFieldValues({});
    setSelectedSiblings([]);
    setSiblingSearch("");
    setSiblingResults([]);
    setFormErrors({});
  };

  const updateGuardian = (index, field, value) => {
    setFormData((f) => {
      const guardians = [...f.guardians];
      guardians[index] = { ...guardians[index], [field]: value };
      return { ...f, guardians };
    });
  };

  const addGuardian = () => setFormData((f) => ({ ...f, guardians: [...f.guardians, emptyGuardian()] }));
  const removeGuardian = (index) => setFormData((f) => ({ ...f, guardians: f.guardians.filter((_, i) => i !== index) }));

  // Sibling linking, right from the registration/edit form — searches
  // existing students and links them once this student is saved (via
  // POST /students/:id/link-siblings). Further sibling management (viewing
  // suggestions, unlinking) stays on the student's profile Siblings tab.
  const searchSiblingCandidates = async (query) => {
    setSiblingSearch(query);
    if (query.trim().length < 2) {
      setSiblingResults([]);
      return;
    }
    setSearchingSiblings(true);
    try {
      const res = await axios.get(`${API_BASE}/students?search=${encodeURIComponent(query)}&limit=8`);
      const excludeIds = new Set([editingStudent?._id, ...selectedSiblings.map((s) => s._id)].filter(Boolean));
      setSiblingResults(res.data.students.filter((s) => !excludeIds.has(s._id)));
    } catch (_) {
      setSiblingResults([]);
    } finally {
      setSearchingSiblings(false);
    }
  };

  const addSelectedSibling = (student) => {
    setSelectedSiblings((prev) => [...prev, student]);
    setSiblingResults((prev) => prev.filter((s) => s._id !== student._id));
    setSiblingSearch("");
  };

  const removeSelectedSibling = (id) => setSelectedSiblings((prev) => prev.filter((s) => s._id !== id));

  const downloadTemplate = () => {
    // Bulk import only accepts .xlsx/.xls/.json, so the template is JSON
    const sample = [
      {
        name: "Jane Doe", rollNumber: "R001", className: "1", section: "A",
        dateOfBirth: "2015-06-01", gender: "female", placeOfBirth: "Hyderabad",
        address: "123 Main St", phone: "9876543210", email: "jane@example.com",
        fatherName: "John Doe", motherName: "Mary Doe", guardianName: "",
        parentPhone: "9876500000", parentPhoneAlt: "", parentOccupation: "Engineer",
        parentName: "John Doe", parentEmail: "john.doe@example.com"
      }
    ];
    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "student_import_template.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportError("");
    setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", importFile);
      const res = await axios.post(`${API_BASE}/students/bulk-import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      fetchStudents();
    } catch (error) {
      setImportError(error.response?.data?.error || "Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (format) => {
    setShowExportMenu(false);
    setExporting(true);
    try {
      const res = await axios.get(`${API_BASE}/students/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `students.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      alert("Failed to export students");
    } finally {
      setExporting(false);
    }
  };

  // Clear status message after 3 seconds
  useEffect(() => {
    if (submitStatus.message) {
      const timer = setTimeout(() => {
        setSubmitStatus({ type: "", message: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">
              Student Management
            </h1>
            <ProfileDropdown />
          </div>
        </nav>

        {/* Status Message — fixed toast so it stays visible above open modals */}
        {submitStatus.message && (
          <div
            className={`fixed top-4 right-4 z-[100] max-w-sm p-4 rounded-lg shadow-lg ${
              submitStatus.type === "success"
                ? "bg-green-100 text-green-700 border border-green-400"
                : "bg-red-100 text-red-700 border border-red-400"
            }`}
          >
            {submitStatus.message}
          </div>
        )}

        <div className="p-6">
          {/* Filters and Actions */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
              <h2 className="text-lg font-semibold">Students List</h2>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu((s) => !s)}
                    disabled={exporting}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                  >
                    {exporting ? "Exporting..." : "Export ▾"}
                  </button>
                  {showExportMenu && (
                    <div className="absolute right-0 mt-1 w-36 bg-white border rounded-lg shadow-lg z-20 overflow-hidden">
                      <button onClick={() => handleExport("csv")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export CSV</button>
                      <button onClick={() => handleExport("xlsx")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export XLS</button>
                      <button onClick={() => handleExport("pdf")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export PDF</button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { setImportFile(null); setImportResult(null); setImportError(""); setShowImportModal(true); }}
                  className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
                >
                  Bulk Import
                </button>
                <button
                  onClick={() => {
                    resetForm();
                    setEditingStudent(null);
                    setShowModal(true);
                    fetchNextAdmissionNumber();
                  }}
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition flex items-center"
                >
                  <span className="mr-2">+</span> Add New Student
                </button>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="text"
                placeholder="Search by name, roll number, or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination({ ...pagination, currentPage: 1 }); // Reset to first page on search
                }}
                className="flex-1 border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <select
                value={filterClass}
                onChange={(e) => {
                  setFilterClass(e.target.value);
                  setPagination({ ...pagination, currentPage: 1 }); // Reset to first page on filter change
                }}
                className="border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Classes</option>
                {classes.map((cls) => (
                  <option key={cls._id} value={cls._id}>
                    Class {cls.className} - Section {cls.section}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Students Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Adm. No</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Roll No</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Gender</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Class</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Father / Mother</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Parent Phone</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                        <div className="flex justify-center items-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          <span>Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : students.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                        <div className="text-4xl mb-2">👥</div>
                        <p>No students found</p>
                        <button
                          onClick={() => { resetForm(); setEditingStudent(null); setShowModal(true); fetchNextAdmissionNumber(); }}
                          className="mt-3 text-indigo-600 hover:text-indigo-800 text-sm"
                        >
                          Add your first student
                        </button>
                      </td>
                    </tr>
                  ) : (
                    students.map((student, idx) => (
                      <tr key={student._id} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-4 text-sm text-gray-500">
                          {(pagination.currentPage - 1) * pagination.pageSize + idx + 1}
                        </td>
                        <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">
                          {student.admissionNumber || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-5 py-4 font-medium whitespace-nowrap">{student.rollNumber}</td>
                        <td className="px-5 py-4 whitespace-nowrap font-semibold text-gray-800">{student.name}</td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${student.gender === 'female' ? 'bg-pink-100 text-pink-700' : student.gender === 'other' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {student.gender ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1) : '—'}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          {student.classId ? (
                            <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                              {student.classId.className} – {student.classId.section}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs italic">Not Assigned</span>
                          )}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          {student.fatherName || student.motherName ? (
                            <div>
                              {student.fatherName && <p className="text-sm font-medium">{student.fatherName}</p>}
                              {student.motherName && <p className="text-xs text-gray-500">{student.motherName}</p>}
                            </div>
                          ) : student.parentId ? (
                            <div>
                              <p className="text-sm font-medium">{student.parentId.name}</p>
                              <p className="text-xs text-gray-400">{student.parentId.email}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs italic">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                          {student.parentPhone || student.phone || <span className="text-gray-400 text-xs">—</span>}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setViewingStudent(student)}
                              title="View"
                              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                            >
                              <IconEye />
                            </button>
                            <button
                              onClick={() => handleEdit(student)}
                              title="Edit"
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition"
                            >
                              <IconEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(student._id)}
                              title="Delete"
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Component */}
            {pagination.totalPages > 0 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                totalItems={pagination.totalItems}
                pageSize={pagination.pageSize}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>

          {/* Results Summary */}
          {!loading && students.length > 0 && (
            <div className="mt-4 text-sm text-gray-500 text-right">
              Showing page {pagination.currentPage} of {pagination.totalPages}
            </div>
          )}
        </div>
      </div>

      {/* View Student Modal — full tabbed profile */}
      {viewingStudent && (
        <StudentProfileModal
          student={viewingStudent}
          classes={classes}
          onClose={() => setViewingStudent(null)}
          onEdit={(s) => { setViewingStudent(null); handleEdit(s); }}
          onStudentUpdated={(updated) => {
            setViewingStudent(updated);
            setStudents((prev) => prev.map((s) => (s._id === updated._id ? { ...s, ...updated } : s)));
          }}
        />
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex justify-between items-center mb-4 px-5 pt-5 pb-3 border-b z-10">
              <h3 className="text-lg font-semibold">
                {editingStudent ? "Edit Student" : "Add New Student"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-600 hover:text-gray-900 text-xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 pb-5 space-y-5">
              <FormSection icon="🎓" title="Student Information">
                <FormField label="Admission Number">
                  <input
                    type="text"
                    readOnly
                    value={generatingAdmissionNo ? "Generating…" : formData.admissionNumber}
                    className={`w-full border rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none ${formErrors.admissionNumber ? "border-red-500" : ""}`}
                  />
                  {formErrors.admissionNumber && <p className="text-xs text-red-500 mt-1">{formErrors.admissionNumber}</p>}
                  {!editingStudent && !formErrors.admissionNumber && <p className="text-xs text-gray-400 mt-1">Auto-generated, cannot be edited</p>}
                </FormField>
                <FormField label="Roll Number *" error={formErrors.rollNumber}>
                  <input
                    type="text"
                    required
                    value={formData.rollNumber}
                    onChange={(e) => setFormData({ ...formData, rollNumber: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.rollNumber ? "border-red-500" : ""}`}
                  />
                </FormField>
                <FormField label="Student Name *" error={formErrors.name} full>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.name ? "border-red-500" : ""}`}
                  />
                </FormField>
                <FormField label="Class *" error={formErrors.classId}>
                  <select
                    required
                    value={formData.classId}
                    onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.classId ? "border-red-500" : ""}`}
                  >
                    <option value="">Select Class</option>
                    {classes.map((cls) => (
                      <option key={cls._id} value={cls._id}>
                        Class {cls.className} - Section {cls.section}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Gender">
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </FormField>
                <FormField label="Date of Birth *" error={formErrors.dateOfBirth}>
                  <input
                    type="date"
                    required
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.dateOfBirth ? "border-red-500" : ""}`}
                  />
                </FormField>
                <FormField label="Place of Birth">
                  <input
                    type="text"
                    value={formData.placeOfBirth}
                    onChange={(e) => setFormData({ ...formData, placeOfBirth: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </FormField>
                <FormField label="Category">
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Category</option>
                    {studentCategories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Fee Group">
                  <select
                    value={formData.feeGroup}
                    onChange={(e) => setFormData({ ...formData, feeGroup: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Fee Group</option>
                    {feeGroups.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </FormField>
              </FormSection>

              <FormSection icon="📍" title="Contact & Address">
                <FormField label="Student Phone">
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="10-digit number"
                  />
                </FormField>
                <FormField label="Student Email" error={formErrors.email}>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.email ? "border-red-500" : ""}`}
                  />
                </FormField>
                <FormField label="Address" full>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows="2"
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </FormField>
              </FormSection>

              <FormSection icon="👪" title="Parent / Guardian Details">
                <FormField label="Father's Name">
                  <input
                    type="text"
                    value={formData.fatherName}
                    onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </FormField>
                <FormField label="Mother's Name">
                  <input
                    type="text"
                    value={formData.motherName}
                    onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </FormField>
                <FormField label="Guardian Name">
                  <input
                    type="text"
                    value={formData.guardianName}
                    onChange={(e) => setFormData({ ...formData, guardianName: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </FormField>
                <FormField label="Parent Occupation">
                  <input
                    type="text"
                    value={formData.parentOccupation}
                    onChange={(e) => setFormData({ ...formData, parentOccupation: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </FormField>
                <FormField label="Parent Phone *" error={formErrors.parentPhone}>
                  <input
                    type="tel"
                    required
                    inputMode="numeric"
                    maxLength={10}
                    value={formData.parentPhone}
                    onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.parentPhone ? "border-red-500" : ""}`}
                    placeholder="10-digit number"
                  />
                </FormField>
                <FormField label="Alternate Phone">
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={formData.parentPhoneAlt}
                    onChange={(e) => setFormData({ ...formData, parentPhoneAlt: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="10-digit number"
                  />
                </FormField>
              </FormSection>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="flex items-center gap-2 font-semibold text-gray-800">
                    <span className="text-lg leading-none">👨‍👩‍👧</span> Additional Guardians
                  </h4>
                  <button type="button" onClick={addGuardian} className="text-sm text-indigo-600 hover:underline">
                    + Add Guardian
                  </button>
                </div>
                {formData.guardians.length === 0 ? (
                  <p className="text-xs text-gray-400">Optional — for guardians beyond father/mother, e.g. a legal guardian or emergency contact.</p>
                ) : (
                  formData.guardians.map((g, i) => (
                    <div key={i} className="bg-white border rounded-lg p-3 space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input type="text" placeholder="Name" value={g.name} onChange={(e) => updateGuardian(i, "name", e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" />
                        <input type="text" placeholder="Relation (e.g. Uncle)" value={g.relation} onChange={(e) => updateGuardian(i, "relation", e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" />
                        <input type="tel" placeholder="Phone" value={g.phone} onChange={(e) => updateGuardian(i, "phone", e.target.value.replace(/\D/g, "").slice(0, 10))} className="border rounded-md px-3 py-1.5 text-sm" />
                        <input type="email" placeholder="Email" value={g.email} onChange={(e) => updateGuardian(i, "email", e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" />
                        <input type="text" placeholder="Occupation" value={g.occupation} onChange={(e) => updateGuardian(i, "occupation", e.target.value)} className="border rounded-md px-3 py-1.5 text-sm sm:col-span-2" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-4 text-xs text-gray-600">
                          <label className="flex items-center gap-1">
                            <input type="checkbox" checked={g.isPrimary} onChange={(e) => updateGuardian(i, "isPrimary", e.target.checked)} /> Primary
                          </label>
                          <label className="flex items-center gap-1">
                            <input type="checkbox" checked={g.isEmergencyContact} onChange={(e) => updateGuardian(i, "isEmergencyContact", e.target.checked)} /> Emergency Contact
                          </label>
                        </div>
                        <button type="button" onClick={() => removeGuardian(i)} className="text-red-500 text-xs hover:underline">Remove</button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-3">
                <h4 className="flex items-center gap-2 font-semibold text-gray-800">
                  <span className="text-lg leading-none">👨‍👩‍👧‍👦</span> Siblings
                </h4>
                <p className="text-xs text-gray-400">
                  Already have a brother or sister enrolled? Search and link them now — the sibling discount applies automatically
                  on future fee generation. (Full sibling management, including unlinking, is on the student's profile after saving.)
                </p>

                {selectedSiblings.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedSiblings.map((s) => (
                      <span key={s._id} className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm">
                        {s.name} ({s.rollNumber})
                        <button type="button" onClick={() => removeSelectedSibling(s._id)} className="text-indigo-400 hover:text-indigo-700">✕</button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative">
                  <input
                    type="text"
                    value={siblingSearch}
                    onChange={(e) => searchSiblingCandidates(e.target.value)}
                    placeholder="Search by student name or roll number…"
                    className="w-full border rounded-md px-3 py-2 text-sm"
                  />
                  {siblingSearch.trim().length >= 2 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {searchingSiblings ? (
                        <p className="px-3 py-2 text-sm text-gray-400">Searching…</p>
                      ) : siblingResults.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-gray-400">No matching students found.</p>
                      ) : (
                        siblingResults.map((s) => (
                          <button
                            type="button"
                            key={s._id}
                            onClick={() => addSelectedSibling(s)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center justify-between"
                          >
                            <span>{s.name} <span className="text-gray-400">({s.rollNumber})</span></span>
                            {s.classId && <span className="text-xs text-gray-400">{s.classId.className}-{s.classId.section}</span>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {customFieldDefs.length > 0 && (
                <FormSection icon="🧩" title="Custom Fields">
                  {customFieldDefs.map((def) => (
                    <FormField key={def.key} label={def.required ? `${def.label} *` : def.label}>
                      {def.fieldType === "dropdown" ? (
                        <select
                          value={customFieldValues[def.key] || ""}
                          onChange={(e) => setCustomFieldValues({ ...customFieldValues, [def.key]: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Select…</option>
                          {def.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : def.fieldType === "checkbox" ? (
                        <input
                          type="checkbox"
                          checked={!!customFieldValues[def.key]}
                          onChange={(e) => setCustomFieldValues({ ...customFieldValues, [def.key]: e.target.checked })}
                          className="h-5 w-5"
                        />
                      ) : (
                        <input
                          type={def.fieldType === "number" ? "number" : def.fieldType === "date" ? "date" : "text"}
                          value={customFieldValues[def.key] ?? ""}
                          onChange={(e) => setCustomFieldValues({ ...customFieldValues, [def.key]: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      )}
                    </FormField>
                  ))}
                </FormSection>
              )}

              <FormSection icon="🔐" title={editingStudent ? "Parent Login Account" : "Parent Login Account (mandatory)"}>
                <FormField label={editingStudent ? "Parent Name" : "Parent Name *"} error={formErrors.parentName}>
                  <input
                    type="text"
                    required={!editingStudent}
                    value={formData.parentName}
                    onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.parentName ? "border-red-500" : ""}`}
                  />
                </FormField>
                <FormField label={editingStudent ? "Parent Email" : "Parent Email *"} error={formErrors.parentEmail}>
                  <input
                    type="email"
                    required={!editingStudent}
                    value={formData.parentEmail}
                    onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.parentEmail ? "border-red-500" : ""}`}
                  />
                </FormField>
                {!editingStudent && (
                  <p className="text-xs text-gray-500 sm:col-span-2">
                    A parent login is created automatically and the credentials are emailed to this address when you save.
                  </p>
                )}
              </FormSection>

              <div className="flex justify-end space-x-3 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition"
                >
                  {editingStudent ? "Update" : "Save"} Student
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Bulk Import Students</h3>
              <button onClick={() => setShowImportModal(false)} className="text-white text-2xl hover:text-indigo-200">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Upload an Excel (.xlsx/.xls) or JSON file. Required fields: <strong>Name, RollNumber, ClassName, Section, DateOfBirth, Gender, ParentName, ParentEmail, ParentPhone</strong>.
                Admission numbers are generated automatically, duplicate roll numbers/parent emails are skipped, and a welcome email is sent to each new parent.
              </p>
              <button type="button" onClick={downloadTemplate} className="text-sm text-indigo-600 hover:underline">
                Download JSON template
              </button>
              <div>
                <input
                  type="file"
                  accept=".xlsx,.xls,.json"
                  onChange={(e) => { setImportFile(e.target.files[0] || null); setImportResult(null); setImportError(""); }}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {importError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{importError}</div>}
              {importResult && (
                <div className="space-y-2">
                  <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {importResult.created} of {importResult.total} student(s) created successfully.
                    {importResult.failed > 0 && ` ${importResult.failed} failed.`}
                  </div>
                  {importResult.failed > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-lg divide-y text-sm">
                      {importResult.results.filter((r) => r.status === 'error').map((r) => (
                        <div key={r.row} className="px-3 py-2 text-red-600">
                          Row {r.row} ({r.rollNumber || r.name || 'unknown'}): {r.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowImportModal(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100">
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleBulkImport}
                  disabled={!importFile || importing}
                  className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {importing ? "Importing..." : "Import"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudents;
